import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, TableSkeleton } from "@/components/common/primitives";
import { Truck, AlertTriangle, CheckCircle2 } from "lucide-react";

const STEPS = ["created", "sent", "carrier pickup", "eta", "delivered", "receiving", "closed"];
const STATUS = { receiving: "text-signal-blue bg-signal-blue/10", closed: "text-ink-muted bg-surface-2", discrepancy: "text-red-600 bg-red-100" };

export default function WFS() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.wfs().then((r) => setRows(r.data)); }, []);
  if (!rows) return <TableSkeleton rows={4} />;

  return (
    <div className="space-y-4">
      <PageHeader title="WFS Shipments" description="Lifecycle tracking with per-SKU discrepancy reconciliation. Delivered ≠ available until received."
        meta={<ProvenanceTag kind="fixture" />} />
      {rows.map((s) => (
        <Panel key={s.id} className="p-4" testid={`wfs-${s.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-ink-muted" /><span className="font-mono font-medium text-ink-primary">{s.id}</span>
              <span className={cn("text-micro font-mono px-1.5 py-0.5 rounded uppercase", STATUS[s.status] || "bg-surface-2")}>{s.status}</span></div>
            <span className="text-micro font-mono text-ink-muted">{s.carrier} · ETA {s.eta}</span>
          </div>
          {/* stepper */}
          <div className="flex items-center gap-1 mb-3 overflow-x-auto ez-scroll">
            {STEPS.map((st, i) => {
              const done = s.status === "closed" || (s.status === "receiving" && i <= 4) || (s.status === "discrepancy" && i <= 4);
              return (
                <React.Fragment key={st}>
                  <span className={cn("text-micro font-mono px-1.5 py-0.5 rounded whitespace-nowrap flex items-center gap-1", done ? "bg-emerald-100 text-emerald-600" : "bg-surface-2 text-ink-muted")}>
                    {done && <CheckCircle2 className="h-3 w-3" />}{st}</span>
                  {i < STEPS.length - 1 && <span className="text-ink-muted text-micro">›</span>}
                </React.Fragment>
              );
            })}
          </div>
          <table className="w-full text-compact">
            <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left border-b border-edge-subtle">
              <th className="py-1 pr-3">SKU</th><th className="pr-3 text-right">Expected</th><th className="pr-3 text-right">Sent</th><th className="pr-3 text-right">Received</th><th className="pr-3 text-right">Damaged</th><th className="pr-3 text-right">Lost</th><th className="text-right">Diff</th>
            </tr></thead>
            <tbody>
              {s.items.map((it) => (
                <tr key={it.sku} className="border-b border-edge-subtle last:border-0">
                  <td className="py-1 pr-3 font-mono">{it.sku}</td><td className="pr-3 text-right font-mono">{it.expected}</td>
                  <td className="pr-3 text-right font-mono">{it.sent}</td><td className="pr-3 text-right font-mono">{it.received}</td>
                  <td className="pr-3 text-right font-mono">{it.damaged}</td><td className="pr-3 text-right font-mono text-red-600">{it.lost}</td>
                  <td className={cn("text-right font-mono", it.diff < 0 ? "text-red-600" : "text-emerald-600")}>{it.diff}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.status === "discrepancy" && (
            <div className="mt-2 flex items-center gap-2 text-micro text-red-600"><AlertTriangle className="h-4 w-4" />Discrepancy — attach evidence & open claim (fixture)</div>
          )}
        </Panel>
      ))}
    </div>
  );
}
